import type { AlertEvent } from "./alert-model.js";

export type TakeoverDecision = "show" | "queued" | "suppressed";

// Bounds the full-screen critical takeover (GUI §6). Only one takeover is on screen at a
// time; genuinely-new criticals that arrive while one is up are QUEUED and shown in turn as
// each is dismissed. A critical that already took over this session is suppressed (the caller
// surfaces it as a capped toast) — so changing home, which replays already-known criticals as
// fresh alerts, can't refill the queue and re-hijack the screen.
// In-memory: a reload clears it (one re-takeover possible, by design — mirrors WatchedZoneNotifier).
export class TakeoverGate {
  #seen = new Set<string>();
  #active = false;
  #queue: AlertEvent[] = [];

  /** Decide what to do with a critical event:
   *  - "show": mount it now (the screen was free).
   *  - "queued": a takeover is already up; it'll mount when the current one is dismissed.
   *  - "suppressed": already shown this session (replay) — caller should toast it instead. */
  request(ev: AlertEvent): TakeoverDecision {
    if (this.#seen.has(ev.subject)) return "suppressed";
    if (this.#active) {
      if (!this.#queue.some((q) => q.subject === ev.subject)) this.#queue.push(ev);
      return "queued";
    }
    this.#open(ev.subject);
    return "show";
  }

  #open(subject: string): void {
    this.#seen.add(subject);
    this.#active = true;
  }

  /** How many criticals are waiting behind the active takeover. */
  queuedCount(): number {
    return this.#queue.length;
  }

  /** The active takeover was dismissed. Returns the next queued event to mount (now marked
   *  active), or null if the queue is empty. */
  next(): AlertEvent | null {
    this.#active = false;
    const ev = this.#queue.shift();
    if (!ev) return null;
    this.#open(ev.subject);
    return ev;
  }

  /** Abandon the takeover flow (the overlay was closed by other means — back button, or the
   *  last dismiss). Drops anything queued but keeps `seen`, so replays still don't re-hijack. */
  abort(): void {
    this.#active = false;
    this.#queue = [];
  }

  /** Clear all state (identity erase / teardown). */
  reset(): void {
    this.#seen.clear();
    this.#active = false;
    this.#queue = [];
  }
}
